<script setup>
import { useMeeting } from '@/composables/useMeeting'
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'
import { useRoute, useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'Meeting',
  },
})

const route = useRoute('meeting-id')
const router = useRouter()
const ability = useAbility()
const { meeting, loading, saving, fetchMeeting, inviteParticipants, finalizeMinutes } = useMeeting()

const load = () => fetchMeeting(route.params.id)

onMounted(load)

const statusColor = value => ({ scheduled: 'info', completed: 'success', cancelled: 'secondary' }[value] ?? 'secondary')

// ── Undang peserta tambahan ──────────────────────────────────────────────
const inviteDialog = ref(false)
const inviteIds = ref([])
const participantOptions = ref([])
const participantSearchLoading = ref(false)

async function searchParticipants(query) {
  participantSearchLoading.value = true
  try {
    const response = await useApi('/api/v1/users', { query: { search: query, per_page: 20 } }).get()
    const data = response?.data ?? response
    const rows = Array.isArray(data) ? data : (data?.data ?? [])

    participantOptions.value = rows.map(u => ({ title: u.name, value: u.id }))
  } catch (e) {
    participantOptions.value = []
  } finally {
    participantSearchLoading.value = false
  }
}

const openInvite = () => { inviteIds.value = []; inviteDialog.value = true }

const doInvite = async () => {
  await inviteParticipants(route.params.id, inviteIds.value)
  inviteDialog.value = false
  await load()
}

// ── Finalisasi notulen ────────────────────────────────────────────────────
const minutesText = ref('')
const minutesError = ref('')

watch(meeting, m => { if (m?.minutes) minutesText.value = m.minutes }, { immediate: true })

const doFinalize = async () => {
  minutesError.value = ''
  try {
    await finalizeMinutes(route.params.id, minutesText.value, [])
    await load()
  } catch (e) {
    minutesError.value = e?.data?.message ?? 'Gagal menyimpan notulen.'
  }
}
</script>

<template>
  <div>
    <div class="d-flex align-center gap-3 mb-4">
      <VBtn
        icon="ri-arrow-left-line"
        variant="text"
        @click="router.push({ name: 'meeting' })"
      />
      <div class="flex-grow-1">
        <div class="d-flex align-center gap-3">
          <h4 class="text-h4 font-weight-bold mb-0">
            {{ meeting?.title }}
          </h4>
          <VChip
            v-if="meeting"
            size="small"
            :color="statusColor(meeting.status)"
            variant="tonal"
          >
            {{ meeting.status }}
          </VChip>
        </div>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ meeting?.scheduled_at }} — {{ meeting?.location ?? 'Lokasi belum diisi' }}
        </p>
      </div>
    </div>

    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <template v-else-if="meeting">
      <VRow>
        <VCol
          cols="12"
          md="7"
        >
          <VCard class="mb-4">
            <VCardTitle class="pa-6 pb-0">
              Agenda
            </VCardTitle>
            <VCardText class="pa-6">
              <VList v-if="meeting.agendas?.length">
                <VListItem
                  v-for="a in meeting.agendas"
                  :key="a.id"
                  :title="a.topic"
                  :subtitle="a.description"
                />
              </VList>
              <p
                v-else
                class="text-medium-emphasis mb-0"
              >
                Tidak ada agenda.
              </p>
            </VCardText>
          </VCard>

          <VCard class="mb-4">
            <VCardTitle class="pa-6 pb-0">
              Notulen
            </VCardTitle>
            <VCardText class="pa-6">
              <VAlert
                v-if="minutesError"
                type="error"
                variant="tonal"
                density="compact"
                class="mb-4"
              >
                {{ minutesError }}
              </VAlert>
              <VTextarea
                v-model="minutesText"
                label="Notulen"
                rows="5"
                :disabled="!ability.can('manage', 'Meeting')"
              />
              <p
                v-if="meeting.minutes_finalized_at"
                class="text-caption text-medium-emphasis"
              >
                Terakhir difinalisasi: {{ meeting.minutes_finalized_at }}
              </p>
            </VCardText>
            <VCardActions
              v-if="ability.can('manage', 'Meeting')"
              class="pa-6 pt-0"
            >
              <VSpacer />
              <VBtn
                color="primary"
                :loading="saving"
                @click="doFinalize"
              >
                Simpan Notulen
              </VBtn>
            </VCardActions>
          </VCard>

          <VCard class="mb-4">
            <VCardTitle class="pa-6 pb-0">
              Tindak Lanjut (Task)
            </VCardTitle>
            <VCardText class="pa-6">
              <TaskList
                taskable-type="meeting"
                :taskable-id="meeting.id"
                scope="all"
              />
            </VCardText>
          </VCard>

          <VCard>
            <VCardTitle class="pa-6 pb-0">
              Lampiran
            </VCardTitle>
            <VCardText class="pa-6">
              <EvidenceUploader
                evidenceable-type="meeting"
                :evidenceable-id="meeting.id"
              />
            </VCardText>
          </VCard>
        </VCol>

        <VCol
          cols="12"
          md="5"
        >
          <VCard>
            <VCardTitle class="pa-6 pb-0 d-flex align-center justify-space-between">
              Daftar Hadir
              <VBtn
                v-if="ability.can('manage', 'Meeting')"
                size="small"
                variant="tonal"
                prepend-icon="ri-user-add-line"
                @click="openInvite"
              >
                Undang
              </VBtn>
            </VCardTitle>
            <VCardText class="pa-6">
              <AttendanceChecklist
                :meeting-id="meeting.id"
                :attendances="meeting.attendances ?? []"
                :read-only="!ability.can('manage', 'Meeting')"
                @saved="load"
              />
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>

    <VDialog
      v-model="inviteDialog"
      max-width="480"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          Undang Peserta
        </VCardTitle>
        <VCardText class="pa-6">
          <VAutocomplete
            v-model="inviteIds"
            :items="participantOptions"
            :loading="participantSearchLoading"
            label="Peserta Baru"
            multiple
            chips
            closable-chips
            @update:search="searchParticipants"
          />
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="inviteDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            :disabled="!inviteIds.length"
            @click="doInvite"
          >
            Undang
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
